import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

const promptCategories = [
  {
    name: 'Content',
    items: [
      { name: 'Ask Anything', path: '/prompts/ask-anything' },
      { name: 'Blog Post', path: '/prompts/blog-post' },
      { name: 'Blog Post Outline', path: '/prompts/blog-post-outline' },
      { name: 'Article', path: '/prompts/article' },
      { name: 'Video Script', path: '/prompts/video-script' },
      { name: 'About Us Generator', path: '/prompts/about-us' }
    ]
  },
  {
    name: 'Social Media',
    items: [
      { name: 'Instagram Post', path: '/prompts/instagram-post' },
      { name: 'Instagram Reel', path: '/prompts/instagram-reel' },
      { name: 'X Thread', path: '/prompts/x-thread' },
      { name: 'LinkedIn Article', path: '/prompts/linkedin-article' }
    ]
  },
  {
    name: 'Marketing',
    items: [
      { name: 'Email Campaign', path: '/prompts/email-campaign' },
      { name: 'Product Description', path: '/prompts/product-description' },
      { name: 'Ad Copy', path: '/prompts/ad-copy' },
      { name: 'Landing Page', path: '/prompts/landing-page' }
    ]
  }
];

function Sidebar() {
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Content']));
  const location = useLocation();

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedCategories(new Set(promptCategories.map(cat => cat.name)));
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  return (
    <div className="w-full sm:w-64 bg-white shadow-lg flex flex-col h-full mt-16">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800">Prompt Categories</h2>
        <div className="flex space-x-4 mt-2">
          <button
            onClick={expandAll}
            className="text-sm text-primary hover:text-primary-dark"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="text-sm text-primary hover:text-primary-dark"
          >
            Collapse All
          </button>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto">
        <div className="py-2">
          {promptCategories.map((category) => (
            <div key={category.name}>
              <button
                onClick={() => toggleCategory(category.name)}
                className="flex w-full items-center justify-between px-4 py-2 text-gray-600 hover:bg-gray-50"
              >
                <span className="font-medium">{category.name}</span>
                <ChevronDownIcon
                  className={`h-5 w-5 transform transition-transform duration-200 ${
                    expandedCategories.has(category.name) ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  expandedCategories.has(category.name) ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="bg-gray-50">
                  {category.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`block px-8 py-2 text-sm ${
                        location.pathname === item.path
                          ? 'text-primary font-medium bg-primary/10'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Feedback Section */}
        <div className="mt-4 border-t border-gray-200">
          <Link
            to="/suggestions"
            className={`flex items-center px-4 py-3 text-sm ${
              location.pathname === '/suggestions'
                ? 'text-primary font-medium bg-primary/10'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            Suggest New Prompts
          </Link>
        </div>
      </nav>
    </div>
  );
}

export default Sidebar;
